import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import DashboardLayout from '../../layouts/DashboardLayout';
import { api } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle 
} from '../../components/ui/dialog';
import { 
  GraduationCap, Loader2, Users, Trophy, Target, Gamepad2, 
  TrendingUp, BarChart3, Award, Mic, FileText, Brain, Clapperboard,
  Puzzle, BookOpen, Sparkles, MessageSquare, X, PenLine
} from 'lucide-react';
import { toast } from 'sonner';

const TeacherClassesPage = () => {
  const { classId } = useParams();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClass, setSelectedClass] = useState(null);
  const [classStats, setClassStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const classesRes = await api.get('/classes');
      setClasses(classesRes.data);
      // Auto-open class dialog if classId is in URL
      if (classId) {
        const targetClass = classesRes.data.find(c => c.id === classId);
        if (targetClass) {
          handleClassClick(targetClass);
        }
      }
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleClassClick = async (cls) => {
    setSelectedClass(cls);
    setDialogOpen(true);
    setLoadingStats(true);
    
    try {
      const response = await api.get(`/classes/${cls.id}/statistics`);
      setClassStats(response.data);
    } catch (err) {
      toast.error('Failed to load class statistics');
      setClassStats(null);
    } finally {
      setLoadingStats(false);
    }
  };

  const StatCard = ({ icon: Icon, label, value, subValue, color, gradient }) => (
    <div className={`relative overflow-hidden rounded-2xl p-4 ${gradient} shadow-lg`}>
      <div className="absolute top-0 right-0 w-20 h-20 transform translate-x-6 -translate-y-6">
        <div className={`w-full h-full rounded-full ${color} opacity-20`}></div>
      </div>
      <Icon className={`h-8 w-8 ${color} mb-2`} />
      <p className="text-2xl font-bold text-white">{value}</p>
      <p className="text-sm text-white/80">{label}</p>
      {subValue && <p className="text-xs text-white/60 mt-1">{subValue}</p>}
    </div>
  );

  const ModuleStatCard = ({ icon: Icon, name, stats, color, bgColor }) => {
    const s = stats || { total_attempts: 0, total_score: 0, avg_score: 0, students_attempted: 0 };
    return (
      <div data-testid={`module-stat-${name.toLowerCase().replace(/\s+/g, '-')}`} className={`${bgColor} rounded-xl p-4 border border-white/10`}>
        <div className="flex items-center gap-3 mb-3">
          <div className={`p-2 rounded-lg ${color}`}>
            <Icon className="h-5 w-5 text-white" />
          </div>
          <h4 className="font-semibold text-white">{name}</h4>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white/5 rounded-lg p-2 text-center">
            <p className="text-xl font-bold text-cyan-400">{s.total_attempts}</p>
            <p className="text-xs text-slate-400">Attempts</p>
          </div>
          <div className="bg-white/5 rounded-lg p-2 text-center">
            <p className="text-xl font-bold text-emerald-400">{s.avg_score}%</p>
            <p className="text-xs text-slate-400">Avg Score</p>
          </div>
        </div>
        <div className="mt-2 flex justify-between text-xs text-slate-400">
          <span>{s.students_attempted} students</span>
          <span>Total: {s.total_score} pts</span>
        </div>
      </div>
    );
  };

  const FunLearningCard = ({ icon: Icon, name, stats, color }) => (
    <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-xl p-3 border border-white/5">
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`h-4 w-4 ${color}`} />
        <span className="text-sm font-medium text-white">{name}</span>
      </div>
      <div className="flex justify-between items-end">
        <div>
          <p className="text-lg font-bold text-white">{stats.total_score}</p>
          <p className="text-xs text-slate-400">Total Points</p>
        </div>
        <div className="text-right">
          <p className="text-sm font-semibold text-emerald-400">{stats.students_played}</p>
          <p className="text-xs text-slate-400">Players</p>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            My Assigned Classes
          </h1>
          <p className="text-slate-500">
            Click on any class to view detailed statistics
          </p>
        </div>

        {classes.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <GraduationCap className="h-12 w-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500 font-medium">No classes assigned yet</p>
              <p className="text-sm text-slate-400 mt-1">
                Contact your administrator to assign classes to your account.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {classes.map((cls) => (
              <Card 
                key={cls.id} 
                data-testid={`class-card-${cls.id}`}
                className="module-card cursor-pointer hover:shadow-xl transition-all duration-300 hover:scale-[1.02] hover:border-indigo-500"
                onClick={() => handleClassClick(cls)}
              >
                <CardContent className="p-6">
                  <div className="inline-flex p-3 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 mb-4 shadow-lg">
                    <GraduationCap className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-1">
                    Class {cls.name}
                  </h3>
                  {cls.description && (
                    <p className="text-xs text-slate-400 mt-2">{cls.description}</p>
                  )}
                  <div className="mt-4 flex items-center gap-2 text-indigo-600 dark:text-indigo-400">
                    <BarChart3 className="h-4 w-4" />
                    <span className="text-sm font-medium">View Statistics</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <div className="mt-4 p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
          <p className="text-sm text-blue-700 dark:text-blue-300">
            <strong>Tip:</strong> Click on any class card to view comprehensive statistics including test performance, fun learning scores, and class leaderboard.
          </p>
        </div>
      </div>

      {/* Class Statistics Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 border-slate-700 text-white p-0">
          {loadingStats ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-12 w-12 animate-spin text-indigo-500" />
            </div>
          ) : classStats ? (
            <>
              {/* Header */}
              <div className="sticky top-0 z-10 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 p-6 rounded-t-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-xl bg-white/20 backdrop-blur">
                      <GraduationCap className="h-8 w-8 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-white">
                        Class {classStats.class_info?.name} Dashboard
                      </h2>
                      <p className="text-white/80">
                        {classStats.total_students} Students Enrolled
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setDialogOpen(false)}
                    className="text-white/80 hover:text-white hover:bg-white/20"
                  >
                    <X className="h-6 w-6" />
                  </Button>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Quick Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <StatCard
                    icon={Users}
                    label="Total Students"
                    value={classStats.total_students}
                    gradient="bg-gradient-to-br from-blue-500 to-blue-700"
                    color="text-blue-200"
                  />
                  <StatCard
                    icon={Target}
                    label="Total Attempts"
                    value={Object.values(classStats.module_stats).reduce((a, b) => a + (b?.total_attempts || 0), 0)}
                    gradient="bg-gradient-to-br from-emerald-500 to-emerald-700"
                    color="text-emerald-200"
                  />
                  <StatCard
                    icon={TrendingUp}
                    label="Avg Score"
                    value={`${Math.round(Object.values(classStats.module_stats).reduce((a, b) => a + (b?.avg_score || 0), 0) / 5)}%`}
                    gradient="bg-gradient-to-br from-amber-500 to-amber-700"
                    color="text-amber-200"
                  />
                  <StatCard
                    icon={Gamepad2}
                    label="Fun Learning"
                    value={Object.values(classStats.fun_learning_stats).reduce((a, b) => a + b.total_score, 0)}
                    subValue="Total Points"
                    gradient="bg-gradient-to-br from-pink-500 to-pink-700"
                    color="text-pink-200"
                  />
                </div>

                {/* Test Modules Performance */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Brain className="h-5 w-5 text-purple-400" />
                    Test Modules Performance
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                    <ModuleStatCard
                      icon={Mic}
                      name="Text to Speech"
                      stats={classStats.module_stats.tts}
                      color="bg-cyan-500"
                      bgColor="bg-gradient-to-br from-cyan-900/50 to-cyan-950/50"
                    />
                    <ModuleStatCard
                      icon={MessageSquare}
                      name="Speech to Text"
                      stats={classStats.module_stats.stt}
                      color="bg-emerald-500"
                      bgColor="bg-gradient-to-br from-emerald-900/50 to-emerald-950/50"
                    />
                    <ModuleStatCard
                      icon={FileText}
                      name="MCQ Quiz"
                      stats={classStats.module_stats.mcq}
                      color="bg-violet-500"
                      bgColor="bg-gradient-to-br from-violet-900/50 to-violet-950/50"
                    />
                    <ModuleStatCard
                      icon={Clapperboard}
                      name="Cinematic MCQ"
                      stats={classStats.module_stats.vmcq}
                      color="bg-rose-500"
                      bgColor="bg-gradient-to-br from-rose-900/50 to-rose-950/50"
                    />
                    <ModuleStatCard
                      icon={PenLine}
                      name="Fill in Blanks"
                      stats={classStats.module_stats.ftb}
                      color="bg-amber-500"
                      bgColor="bg-gradient-to-br from-amber-900/50 to-amber-950/50"
                    />
                  </div>
                </div>

                {/* Fun Learning Stats */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Gamepad2 className="h-5 w-5 text-pink-400" />
                    Fun Learning Games
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <FunLearningCard
                      icon={Puzzle}
                      name="Picture Match"
                      stats={classStats.fun_learning_stats.picture_match}
                      color="text-cyan-400"
                    />
                    <FunLearningCard
                      icon={BookOpen}
                      name="Context Word"
                      stats={classStats.fun_learning_stats.context_word}
                      color="text-violet-400"
                    />
                    <FunLearningCard
                      icon={Sparkles}
                      name="Spelling Bee"
                      stats={classStats.fun_learning_stats.spelling_bee}
                      color="text-amber-400"
                    />
                    <FunLearningCard
                      icon={FileText}
                      name="Sentence Builder"
                      stats={classStats.fun_learning_stats.sentence_builder}
                      color="text-emerald-400"
                    />
                  </div>
                </div>

                {/* Leaderboard */}
                <div>
                  <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Trophy className="h-5 w-5 text-amber-400" />
                    Class Leaderboard - Top 10
                  </h3>
                  <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-xl overflow-hidden border border-white/10">
                    {classStats.leaderboard.length === 0 ? (
                      <div className="py-8 text-center text-slate-400">
                        <Trophy className="h-12 w-12 mx-auto mb-2 opacity-30" />
                        <p>No scores recorded yet</p>
                      </div>
                    ) : (
                      <div className="divide-y divide-white/5">
                        {classStats.leaderboard.map((student) => (
                          <div
                            key={student.student_id}
                            className={`flex items-center gap-4 p-4 hover:bg-white/5 transition-colors ${
                              student.rank <= 3 ? 'bg-gradient-to-r from-amber-500/10 to-transparent' : ''
                            }`}
                          >
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg ${
                              student.rank === 1 ? 'bg-gradient-to-br from-amber-400 to-amber-600 text-white' :
                              student.rank === 2 ? 'bg-gradient-to-br from-slate-300 to-slate-500 text-white' :
                              student.rank === 3 ? 'bg-gradient-to-br from-amber-600 to-amber-800 text-white' :
                              'bg-slate-700 text-slate-300'
                            }`}>
                              {student.rank <= 3 ? (
                                <Award className="h-5 w-5" />
                              ) : (
                                student.rank
                              )}
                            </div>
                            <div className="flex-1">
                              <p className="font-semibold text-white">{student.name}</p>
                              <p className="text-xs text-slate-400">
                                ID: {student.student_id} • Roll: {student.roll_no || '-'}
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="text-xl font-bold text-emerald-400">{student.total_score}</p>
                              <p className="text-xs text-slate-400">{student.total_attempts} attempts</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="py-20 text-center text-slate-400">
              <p>Failed to load statistics</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default TeacherClassesPage;
